import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/auth.service';
import { useSignInProviders } from '../../hooks/useSignInProviders';
import { getApiErrorMessage } from '../../lib/apiError';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';
import { PasswordField, PolicyChecklist } from './NewPasswordFields';
import { policyPasses } from './passwordPolicy';
import SignInProviderButtons from './SignInProviderButtons';

/** The message for the first thing that stops a submission, or null when none does. */
function submissionBlocker(token: string, allValid: boolean): string | null {
  if (!token) return 'Invitation token is missing.';
  if (!allValid) {
    return 'Password does not meet the policy or does not match the confirmation.';
  }
  return null;
}

/** The two documents, linked and never ticked here: the account is created without accepting
 *  them, and the first sign-in asks for both on one screen (owner, 2026-10-28). */
function DocumentLinks(): JSX.Element {
  return (
    <p className="mt-5 text-center text-xs text-gray-400">
      <Link to="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2">
        Terms and Conditions
      </Link>
      {' · '}
      <Link
        to="/privacy-notice"
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2"
      >
        Privacy notice
      </Link>
    </p>
  );
}

/** The submit button: gold while it can be pressed, grey while something is still missing. */
function ActivateButton({ disabled, loading }: Readonly<{ disabled: boolean; loading: boolean }>): JSX.Element {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="w-full py-3 rounded-xl font-semibold text-sm transition-all mt-2"
      style={{
        background: disabled ? '#E5E7EB' : `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
        color: disabled ? '#9CA3AF' : NAVY,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {loading ? 'Activating...' : 'Activate account'}
    </button>
  );
}

/**
 * Public page reached from the invitation email link
 * (`/accept-invitation#token=...`: the fragment, which no access log sees, #329).
 * Redeems the one-time token and sets the client's password via POST
 * /auth/accept-invitation, then sends them to sign in. Mirrors ResetPassword —
 * same token-from-the-fragment + password-policy pattern;
 * the first/last name come from the invitation row, so only a password is
 * collected here.
 *
 * Under the password form the page also offers the sign-in providers this
 * environment has (Google, Microsoft, Apple). Choosing one redeems the same
 * invitation as part of that sign-in, so the account is created without a
 * password; the invitation token goes along in the request body.
 */
export default function AcceptInvitation(): JSX.Element {
  const navigate = useNavigate();
  // Read once, at mount; the address bar is cleared right after (`lib/linkToken`).
  const [token] = useState(() => readLinkToken());
  useEffect(() => clearLinkFromAddressBar(), []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const providers = useSignInProviders();

  const passes = useMemo(() => policyPasses(password), [password]);
  const allValid = passes.every(Boolean) && password === confirm;
  const inputsDisabled = loading || !token;
  const submitDisabled = loading || !allValid || !token;
  const confirmMatches = confirm.length > 0 && password === confirm;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const blocker = submissionBlocker(token, allValid);
    if (blocker) {
      setError(blocker);
      return;
    }
    setLoading(true);
    try {
      await authService.acceptInvitation(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Invalid or expired invitation link.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl p-9"
          style={{ background: '#FFFFFF', boxShadow: '0 32px 80px rgba(0,0,0,0.45)' }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Activate your account</h2>
          <p className="text-gray-400 text-sm mb-7">
            Welcome to the QMI Investor Portal. Choose a secure password to finish setting up your account.
          </p>

          {!token && (
            <div className="mb-5 p-3 rounded-lg text-sm"
                 style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
              This link does not contain an invitation token. Please use the link from your invitation email.
            </div>
          )}

          {success ? (
            <div className="p-4 rounded-lg text-sm"
                 style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
              <p className="font-medium mb-1">Account activated</p>
              <p className="text-xs">Redirecting you to sign in...</p>
            </div>
          ) : (
            <>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg text-sm"
                     style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="accept-password" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Password
                </label>
                <PasswordField
                  id="accept-password"
                  value={password}
                  onChange={setPassword}
                  disabled={inputsDisabled}
                  show={showPassword}
                  onToggleShow={() => setShowPassword((s) => !s)}
                />
              </div>

              <div>
                <label htmlFor="accept-confirm-password" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Confirm password
                </label>
                <input
                  id="accept-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F8F9FC', border: '1.5px solid #E8ECF4', color: '#111827' }}
                  placeholder="Repeat the password"
                  required
                  maxLength={128}
                  disabled={inputsDisabled}
                />
              </div>

              <PolicyChecklist passes={passes} confirmMatches={confirmMatches} />

              <ActivateButton disabled={submitDisabled} loading={loading} />
            </form>

            {/* The other way to activate: one of the accounts the person
                already has. Offered only with an invitation to redeem —
                without one there is nothing for these buttons to activate. */}
            {token && providers.length > 0 && (
              <div className="mt-6 pt-5" style={{ borderTop: '1px solid #F0F0F0' }}>
                <p className="text-sm text-gray-500 mb-3 text-center">
                  Set a password above, or continue with one of these accounts instead.
                </p>
                <SignInProviderButtons
                  providers={providers}
                  invitationToken={token}
                  disabled={loading}
                />
              </div>
            )}
            </>
          )}

          <DocumentLinks />

          <p className="mt-6 text-center text-xs text-gray-400">
            <Link to="/login" className="font-medium" style={{ color: GOLD }}>
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
